import { type ReactNode } from 'react';
import { fmtMoney } from '../lib/format.js';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { newerBuildAvailable, runningBundle, servedBundle } from '../lib/update-check.js';
import { api } from '../lib/api.js';
import { ThemeButton } from './ThemeButton.js';
import { StatsToasts } from './StatsToasts.js';
import { pathForPane } from '../pages/settings/panes.js';
import type { Attention, AttentionSummary, Me } from '../lib/types.js';

interface Props {
  path: string;
  navigate: (to: string) => void;
  username: string;
  children: ReactNode;
}

/** Where the header's own-name link goes. */
const ACCOUNT_PATH = pathForPane('account');

export function Layout({ path, navigate, username, children }: Props) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/auth/me') });
  // What needs a person, for the indicator by the username and the nav
  // badges: its own light query, polled, so it is right on every page and
  // not only after the Stats page has loaded a summary (2026-09-15).
  const attn = useQuery({
    queryKey: ['attention'],
    queryFn: () => api.get<AttentionSummary>('/api/attention'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const attention: Attention | undefined =
    attn.data ??
    (
      qc
        .getQueriesData<{ attention?: Attention }>({ queryKey: ['summary'] })
        .find(([, d]) => d?.attention)?.[1] as { attention?: Attention } | undefined
    )?.attention;

  const signOut = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['auth-status'] }),
  });

  // A newer build than the one this tab runs (lib/update-check.ts): a pill by
  // the name, and the next page click is a full load instead of an in-app one.
  const running = runningBundle();
  const served = useQuery({
    queryKey: ['served-bundle'],
    queryFn: servedBundle,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    enabled: running != null,
  });
  const stale = newerBuildAvailable(served.data ?? null, running);
  const go = (to: string) => {
    if (stale) window.location.assign(to);
    else navigate(to);
  };

  const onConfig = path.startsWith('/configuration');
  const link = (to: string, label: string, badge?: number, key?: string) => {
    // Stats is the page whenever no other page is: never Costs, Matching or
    // Configuration (the old test forgot Costs, so both lit up there).
    const current =
      to === '/'
        ? path !== '/matching' && path !== '/flagged' && path !== '/costs' && path !== '/daily' && !onConfig
        : to === '/configuration'
          ? onConfig
          : path === to;
    return (
      <a
        key={key ?? to}
        href={to}
        aria-current={current ? 'page' : undefined}
        onClick={(e) => {
          e.preventDefault();
          go(to);
        }}
      >
        {label}
        {badge ? <span className="badge">{badge}</span> : null}
      </a>
    );
  };

  // What a person can match on the Matching page right now: suggested links
  // still open (or spending) and registrations with a feed to link to - every
  // row a click resolves (2026-09-17: the count used to leave suggestions out,
  // so confirming two moved nothing). Registrations no source carries and
  // destinations pushed without a feed wait on a source or on Airman: listed
  // there, never counted (owner 2026-09-18: "13 to match but I can only see 1").
  const matchingBadge = attention
    ? attention.matchingUnresolved + (attention.matchingSuggested ?? 0)
    : 0;
  const waiting = attention?.matchingWaiting ?? 0;
  const quiet = attention?.matchingNoRevenue;
  // What needs attention on the Flagged page: matched feeds losing money or
  // spending with no revenue on confirmed links (owner ask 2026-09-18).
  const losing = attention?.matchingLosing;
  const flaggedBadge = (quiet?.feeds ?? 0) + (losing?.feeds ?? 0);
  const configBadge = attention ? attention.pendingConfirmations : 0;
  // The indicator's items: each says what and where. A failing source names
  // its error's kind so the fix is obvious (a layout to re-learn, a key to
  // replace, a provider to wait for).
  const items: { key: string; text: string; to: string; kind?: string; title?: string }[] = [];
  const airmanSync = attn.data?.airmanSync;
  if (airmanSync) {
    items.push({
      key: 'airman-sync',
      text: `Airman sync: ${airmanSync.error ? 'failing' : 'stale'}`,
      to: '/configuration/buy',
      kind: 'failing',
      title: airmanSync.error ?? (airmanSync.lastSync ? `last successful sync ${airmanSync.lastSync}` : 'never synced'),
    });
  }
  for (const a of attn.data?.accountsFailing ?? []) {
    const what =
      a.kind === 'layout'
        ? 'layout — re-learn it'
        : a.kind === 'credentials'
          ? 'credentials rejected'
          : a.kind === 'network'
            ? 'not reachable'
            : 'failing';
    items.push({
      key: `acct:${a.id}`,
      text: `${a.label}: ${what}`,
      to: '/configuration/sources',
      kind: a.kind ?? 'failing',
      title: a.error,
    });
  }
  if (losing?.feeds)
    items.push({
      key: 'losing',
      text: `${losing.feeds} feed${losing.feeds === 1 ? '' : 's'} losing money (−${fmtMoney(Math.abs(losing.net7d))} net in 7 days)`,
      to: '/flagged',
      kind: 'failing',
      title:
        'confirmed destinations spent more in the last 7 days than the feed earned - pause or lower them in Airman, or check the payout with the source',
    });
  if (quiet?.feeds)
    items.push({
      key: 'no-revenue',
      text: `${quiet.feeds} feed${quiet.feeds === 1 ? '' : 's'} spending with no revenue (${fmtMoney(quiet.spend7d)} in 7 days)`,
      to: '/flagged',
      kind: 'failing',
      title:
        'confirmed destinations spent in the last 7 days on a feed that reported no payout - check its source, its channel and its URL',
    });
  if (matchingBadge) {
    const parts = [
      attention?.matchingSuggested
        ? `${attention.matchingSuggested} suggested link${attention.matchingSuggested === 1 ? '' : 's'} to confirm`
        : '',
      attention?.matchingUnresolved
        ? `${attention.matchingUnresolved} registration${attention.matchingUnresolved === 1 ? '' : 's'} with a source to link`
        : '',
    ].filter(Boolean);
    items.push({
      key: 'matching',
      text: `${matchingBadge} to match`,
      to: '/matching',
      title: parts.join(' · '),
    });
  }
  if (waiting)
    items.push({
      key: 'waiting',
      text: `${waiting} destination${waiting === 1 ? '' : 's'} waiting on a source or on Airman`,
      to: '/matching',
      title:
        'registrations no stats source reports, and destinations Airman pushed without a feed - add the source, or fix the channel in Airman; nothing to match to yet, so not counted',
    });
  // Two queues, two panes: a queued source's layout is confirmed under Add stats, an email report under Email & Sheets.
  const pendingSources =
    attention?.pendingSources ?? (attention?.pendingEmails == null ? configBadge : 0);
  const pendingEmails = attention?.pendingEmails ?? 0;
  if (pendingSources)
    items.push({
      key: 'pending',
      text: `${pendingSources} layout${pendingSources === 1 ? '' : 's'} waiting to be confirmed`,
      to: '/configuration/add',
    });
  if (pendingEmails)
    items.push({
      key: 'pending-email',
      text: `${pendingEmails} email report${pendingEmails === 1 ? '' : 's'} waiting to be confirmed`,
      to: '/configuration/ingest#email-pending',
    });
  if (attention?.duplicateCredentials)
    items.push({
      key: 'dupes',
      text: `${attention.duplicateCredentials} credential${attention.duplicateCredentials === 1 ? '' : 's'} on more than one source`,
      to: '/configuration/sources',
    });
  // Airman vs the bill: a judged day outside tolerance stays here until it matches (alerts/reconcile-alert.ts says it once to the webhook).
  const rec = attn.data?.reconcile;
  if (rec && rec.days.length) {
    items.push({
      key: 'reconcile',
      text: `Airman vs the bill: ${rec.days.length} of the last ${rec.judged} day${rec.judged === 1 ? '' : 's'} outside ±5%`,
      to: '/costs',
      kind: 'reconcile',
      title: rec.days
        .map(
          (d) =>
            `${d.day}: cost ${d.costGapPct == null ? '—' : `${d.costGapPct > 0 ? '+' : ''}${d.costGapPct.toFixed(1)}%`} (${d.flag}) · launches ${d.undetectLaunches.toLocaleString('en-US')} vs visits ${d.airmanVisits.toLocaleString('en-US')} (${d.volumeFlag})`,
        )
        .join('\n'),
    });
  }

  return (
    <div className="shell">
      <div className="topbar">
        <div className="brand">
          {/* The file keeps its name, so a browser and the CDN hold it for the 4-hour
              max-age; the query is bumped when the picture changes (feet, 2026-09-15). */}
          <img
            className="brandmark"
            src="/pidgeon-logo.png?v=2"
            alt="Traffic"
            title="The Pidgons Are Trained"
          />
          <span className="eyebrow">Traffic</span>
          <nav className="topnav" aria-label="Pages">
            {link('/', 'Dashboard')}
            {link('/daily', 'Daily')}
            {link('/costs', 'Costs')}
            {link('/matching', 'Matching', matchingBadge)}
            {link('/flagged', 'Flagged', flaggedBadge)}
            {link('/configuration', 'Configuration', configBadge)}
          </nav>
        </div>
        <div className="userbox">
          {/* Your own name is the obvious place to click to get at your own settings, so it goes
              there. Labelled with the destination rather than just the name, because "Ashton" alone
              tells a screen reader nothing about what activating it does — but NOT with the string
              "My account", which is the sidebar link's name: role+name lookups match by substring,
              so the two would be indistinguishable to a screen-reader user and to the e2e suite. */}
          <a
            className="who"
            href={ACCOUNT_PATH}
            aria-current={path === ACCOUNT_PATH ? 'page' : undefined}
            aria-label={`Your account: ${me.data?.rep ?? username}`}
            onClick={(e) => {
              e.preventDefault();
              go(ACCOUNT_PATH);
            }}
          >
            {me.data?.rep ?? username}
          </a>
          {stale ? (
            <button
              type="button"
              className="pill gold update-pill"
              title="This tab is running an older build than the one now served; reload to pick up the new one"
              onClick={() => window.location.reload()}
            >
              New version — reload
            </button>
          ) : null}
          {/* Needs attention: a count by the name, the list on click, each line a way there. */}
          {items.length ? (
            <details className="attn">
              <summary
                aria-label={`${items.length} thing${items.length === 1 ? '' : 's'} need attention`}
                title="What needs a person"
              >
                <span className="attn-count">{items.length}</span>
                <span className="attn-word">need{items.length === 1 ? 's' : ''} attention</span>
              </summary>
              <ul className="attn-list">
                {items.map((it) => (
                  <li key={it.key} data-kind={it.kind ?? ''}>
                    <a
                      href={it.to}
                      title={it.title}
                      onClick={(e) => {
                        e.preventDefault();
                        (
                          e.currentTarget.closest('details') as HTMLDetailsElement | null
                        )?.removeAttribute('open');
                        go(it.to);
                      }}
                    >
                      {it.text}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <button className="ghost small" onClick={() => signOut.mutate()}>
            Sign out
          </button>
          <ThemeButton />
        </div>
      </div>
      {children}
      {/* "Stats just came in": whose numbers arrived, from which provider and
          how much they moved (owner ask 2026-09-21). Mounted here so it is on
          every page and never on the login screen, and given the same `go` as
          the nav so a click follows a pending reload like any other link. */}
      <StatsToasts go={go} />
      {/* Phone (2026-09-14 mobile design): the pages sit in a bar under the
          thumb. Same links and badges as the top nav; app.css shows this one
          and hides the other at 640px and under, so only one is ever in the
          accessibility tree. */}
      <nav className="tabbar" aria-label="Pages (phone)">
        {link('/', 'Dashboard', undefined, 'tab-stats')}
        {link('/daily', 'Daily', undefined, 'tab-daily')}
        {link('/costs', 'Costs', undefined, 'tab-costs')}
        {link('/matching', 'Matching', matchingBadge, 'tab-matching')}
        {link('/flagged', 'Flagged', flaggedBadge, 'tab-flagged')}
        {link('/configuration', 'Config', configBadge, 'tab-config')}
      </nav>
    </div>
  );
}
